import BpkLargeSearchIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/search';
import ChevronIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/chevron-right';
import BpkSmlSearchIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/search';
import BpkText, {
  TEXT_STYLES,
  TEXT_COLORS,
} from '@skyscanner-internal/backpack-web/bpk-component-text';

import { useMBDContext } from '../../common/MBD/useMBDContext';

import STYLES from './PriceBar.module.scss';

const getPriceLabel = (position: string, priceText: string) => (
  <div className={STYLES[`PriceLabel__position-${position}`]}>
    <BpkText
      color={TEXT_COLORS.textOnDark}
      textStyle={TEXT_STYLES.label3}
      className={STYLES.PriceLabel__tooltipPrice}
    >
      {priceText}
    </BpkText>
    <ChevronIcon
      className={STYLES.PriceLabel__chevronArrow}
      style={{ width: 12, height: 12 }}
    />
  </div>
);

const getRatio = (price: number, maxPrice: number) => {
  if (price > maxPrice) {
    return 100;
  }
  return (price / maxPrice) * 100;
};

const PriceBar = ({
  isCheapest,
  maxPrice,
  monthIndex,
  noPriceText,
  price,
  priceAriaLabel,
  priceText,
  url,
  useV2,
}: PriceBarProps) => {
  const { buildMBDParam, rawComponentName } = useMBDContext();
  const mbdComponentName = `${rawComponentName}_month`;

  const searchIconStyle = !useV2 ? (
    <BpkLargeSearchIcon className={STYLES['search-icon']} />
  ) : (
    <BpkSmlSearchIcon className={STYLES['search-icon']} />
  );

  if (!price) {
    return (
      <div className={STYLES.PriceBar}>
        <a
          href={url}
          className={STYLES['no-price']}
          tabIndex={0}
          aria-label={noPriceText}
          rel="nofollow"
          data-tracking-element-id={mbdComponentName}
          data-tracking-common-params={buildMBDParam(mbdComponentName)}
        >
          {searchIconStyle}
          {noPriceText}
        </a>
      </div>
    );
  }

  const ratio = getRatio(price, maxPrice);
  const isLabelInBar = ratio > 50;
  let barStyle = isCheapest ? STYLES['bar-cheapest'] : STYLES['bar-regular'];

  if (price > maxPrice) {
    barStyle += ` ${STYLES.outlier}`;
  }

  return (
    <div className={STYLES.PriceBar}>
      <a
        href={url}
        rel="nofollow"
        className={STYLES.container}
        aria-label={priceAriaLabel}
        data-tracking-element-id={`${mbdComponentName}_${monthIndex}`}
        data-tracking-common-params={buildMBDParam(mbdComponentName)}
      >
        <div className={barStyle} style={{ width: `${ratio}%` }}>
          {isLabelInBar && getPriceLabel('left', priceText)}
        </div>
        {!isLabelInBar && getPriceLabel('right', priceText)}
      </a>
    </div>
  );
};

type PriceBarProps = {
  price: number | undefined;
  maxPrice: number;
  url: string;
  priceText: string;
  noPriceText: string;
  isCheapest: boolean;
  priceAriaLabel: string;
  monthIndex: number;
  useV2: boolean;
};

export default PriceBar;
